import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicNav, PublicFooter } from './founding/PublicChrome';
import { Footer } from './Footer';

export const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    projectType: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email';
    if (!formData.projectType) newErrors.projectType = 'Please select a project type';
    if (!formData.message.trim()) newErrors.message = 'Please tell us about your project';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setSubmitting(true);
    // Simulate submission delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSubmitting(false);
    setSubmitted(true);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--color-bg)' }}>
      {/* Navigation */}
      <PublicNav cta="estimate" />

      {/* Main Content */}
      <main className="contact-page flex-1" style={{ paddingTop: '120px' }}>
        <div className="contact-container">
          {/* Header */}
          <div className="contact-header">
            <p className="contact-label">GET IN TOUCH</p>
            <h1 style={{ color: 'var(--color-text)', marginBottom: '16px' }}>Let's talk about your project</h1>
            <p style={{ fontSize: '18px', color: 'var(--color-muted)', lineHeight: '1.7', maxWidth: '520px', margin: '0 auto' }}>
              Tell us about your project and we'll come back to you within one working day.
            </p>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid var(--color-border)', margin: '48px 0' }} />

          {/* Two Columns */}
          <div className="contact-columns">
            {/* Left Column - Email */}
            <div>
              <p className="contact-label" style={{ color: '#9CA3AF', marginBottom: '12px' }}>EMAIL US DIRECTLY</p>
              <a 
                href="mailto:team@schedulesofwork.com" 
                style={{ 
                  color: 'var(--color-navy)', 
                  fontSize: '17px', 
                  fontWeight: '500', 
                  textDecoration: 'none',
                  display: 'block',
                  marginBottom: '12px'
                }}
                className="hover:underline"
              >
                team@schedulesofwork.com
              </a>
              <p style={{ fontSize: '14px', color: 'var(--color-muted)', lineHeight: '1.6' }}>
                Prefer to write directly? Drop us an email and we'll come back to you within one working day.
              </p>
            </div>

            {/* Right Column - Form */}
            <div className="contact-form-card">
              {submitted ? (
                <div className="contact-success">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="24" cy="24" r="22" stroke="#0F2742" strokeWidth="2"/>
                    <path d="M14 24L21 31L34 17" stroke="#0F2742" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3 style={{ fontWeight: '600', marginTop: '20px', color: 'var(--color-text)' }}>Message received</h3>
                  <p style={{ fontSize: '16px', color: 'var(--color-muted)', marginTop: '8px' }}>
                    Thanks for getting in touch. We'll get back to you within one working day.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="form-field">
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      className={`form-input ${errors.fullName ? 'error' : ''}`}
                      value={formData.fullName}
                      onChange={(e) => handleChange('fullName', e.target.value)}
                    />
                    {errors.fullName && <p className="form-error">{errors.fullName}</p>}
                  </div>

                  <div className="form-field">
                    <label className="form-label">Email Address *</label>
                    <input
                      type="email"
                      className={`form-input ${errors.email ? 'error' : ''}`}
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                    />
                    {errors.email && <p className="form-error">{errors.email}</p>}
                  </div>

                  <div className="form-field">
                    <label className="form-label">Project Type *</label>
                    <select
                      className={`form-select ${errors.projectType ? 'error' : ''}`}
                      value={formData.projectType}
                      onChange={(e) => handleChange('projectType', e.target.value)}
                    >
                      <option value="">Select a project type</option>
                      <option value="residential">Residential</option>
                      <option value="commercial">Commercial</option>
                      <option value="new-build">New Build</option>
                      <option value="refurbishment">Refurbishment</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.projectType && <p className="form-error">{errors.projectType}</p>}
                  </div>

                  <div className="form-field">
                    <label className="form-label">Tell us about your project *</label>
                    <textarea
                      className={`form-textarea ${errors.message ? 'error' : ''}`}
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                    />
                    {errors.message && <p className="form-error">{errors.message}</p>}
                  </div>

                  <button type="submit" className="form-submit" disabled={submitting}>
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>

                  <p className="form-note">We aim to respond within one working day.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <PublicFooter fallback={<Footer />} />
    </div>
  );
};
